// src/pages/admin/edit-taller.tsx
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { getDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { Plus, Trash2 } from 'lucide-react';
import { calcularEstadoTaller } from '../../utils/tallerEstadoHelper';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import PageTitle from '../../components/page-title';
import { LoadingMessage } from '../../components/loading-states';

interface Ponente {
  nombre: string;
  cargo: string;
  empresa: string;
  tema: string;
  horario: string;
}

interface Patrocinador {
  nombre: string;
  tipo: 'aliado' | 'patrocinador' | 'expositor';
  logo: string;
  link: string;
}

interface AgendaItem {
  horario: string;
  actividad: string;
  descripcion: string;
}

const actividadesOpciones = [
  'Conferencias',
  'Talleres',
  'Paneles',
  'Networking',
  'Comida',
  'Exposición de desarrollos',
  'Presentación de marca'
];

const schema = Yup.object().shape({
  nombre: Yup.string().required('El nombre del evento es obligatorio'),
  fecha: Yup.date().typeError('Fecha inválida').required('La fecha del evento es obligatoria'),
  horaInicio: Yup.string()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato de hora inválido (ej. 14:00)')
    .required('La hora de inicio es obligatoria'),
  horaTermino: Yup.string()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato de hora inválido (ej. 18:00)')
    .required('La hora de término es obligatoria')
    .test(
      'hora-termino-posterior',
      'La hora de término debe ser posterior a la de inicio',
      function (value) {
        const { horaInicio } = this.parent;
        if (!horaInicio || !value) return true;
        const m1 = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(horaInicio);
        const m2 = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
        if (!m1 || !m2) return true;
        const ini = parseInt(m1[1], 10) * 60 + parseInt(m1[2], 10);
        const fin = parseInt(m2[1], 10) * 60 + parseInt(m2[2], 10);
        return fin > ini;
      }
    ),
  lugar: Yup.string().required('El lugar es obligatorio'),
  direccion: Yup.string().required('La dirección es obligatoria'),
  ciudad: Yup.string().required('La ciudad es obligatoria'),
  organizadorPrincipal: Yup.string().required('El organizador es obligatorio'),
  responsableNombre: Yup.string().required('El nombre del responsable es obligatorio'),
  responsableCargo: Yup.string().required('El cargo del responsable es obligatorio'),
  responsableCorreo: Yup.string().email('Correo inválido'),
  responsableTelefono: Yup.string(),
  tipoEvento: Yup.string().required('El tipo de evento es obligatorio'),
  enlaceVirtual: Yup.string().url('Debe ser un enlace válido (Zoom, Teams, etc.)'),
  objetivo: Yup.string().required('El objetivo es obligatorio'),
  publicoObjetivo: Yup.string().required('El público objetivo es obligatorio'),
  descripcionGeneral: Yup.string().required('La descripción es obligatoria'),
  cupoMaximo: Yup.number().positive().required('El cupo es obligatorio'),
  tipoCosto: Yup.string().required('El tipo de costo es obligatorio'),
  precio: Yup.number().min(0),
  moneda: Yup.string(),
  creditos: Yup.number().min(0),
  actividades: Yup.array(),
  otraActividad: Yup.string(),
  requiereRegistro: Yup.boolean(),
  infoRecopilar: Yup.string(),
  elementosDestacados: Yup.string(),
  linkRegistro: Yup.string().url('Debe ser un enlace válido').nullable().transform(v => v || null),
  landingPage: Yup.string().url('Debe ser un enlace válido').nullable().transform(v => v || null),
  eventoFacebook: Yup.string().url('Debe ser un enlace válido').nullable().transform(v => v || null),
  bancoTitular: Yup.string(),
  bancoNombre: Yup.string(),
  bancoCuenta: Yup.string(),
  bancoClabe: Yup.string(),
  bancoReferencia: Yup.string(),
  bancoInstrucciones: Yup.string()
});

export default function EditTaller() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [taller, setTaller] = useState<any>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [currentBannerUrl, setCurrentBannerUrl] = useState<string>('');
  const [estadoManual, setEstadoManual] = useState<string>('');
  const [tipoEventoActual, setTipoEventoActual] = useState<string>('presencial');
  const [tipoCostoActual, setTipoCostoActual] = useState<string>('gratuito');
  const [requiereRegistroActual, setRequiereRegistroActual] = useState<boolean>(false);
  const [mostrarEnlaces, setMostrarEnlaces] = useState<boolean>(false);
  const [ponentes, setPonentes] = useState<Ponente[]>([]);
  const [patrocinadores, setPatrocinadores] = useState<Patrocinador[]>([]);
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [actividades, setActividades] = useState<string[]>([]);
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const handleActividadChange = (actividad: string) => {
    const nuevas = actividades.includes(actividad)
      ? actividades.filter(a => a !== actividad)
      : [...actividades, actividad];
    setActividades(nuevas);
    setValue('actividades', nuevas);
  };

  const agregarPonente = () => setPonentes([...ponentes, { nombre: '', cargo: '', empresa: '', tema: '', horario: '' }]);
  const actualizarPonente = (i: number, campo: keyof Ponente, val: string) => {
    const n = [...ponentes];
    n[i] = { ...n[i], [campo]: val };
    setPonentes(n);
  };
  const eliminarPonente = (i: number) => setPonentes(ponentes.filter((_, idx) => idx !== i));

  const agregarPatrocinador = () => setPatrocinadores([...patrocinadores, { nombre: '', tipo: 'patrocinador', logo: '', link: '' }]);
  const actualizarPatrocinador = (i: number, campo: keyof Patrocinador, val: string) => {
    const n = [...patrocinadores];
    n[i] = { ...n[i], [campo]: val } as Patrocinador;
    setPatrocinadores(n);
  };
  const eliminarPatrocinador = (i: number) => setPatrocinadores(patrocinadores.filter((_, idx) => idx !== i));

  const agregarAgendaItem = () => setAgenda([...agenda, { horario: '', actividad: '', descripcion: '' }]);
  const actualizarAgendaItem = (i: number, campo: keyof AgendaItem, val: string) => {
    const n = [...agenda];
    n[i] = { ...n[i], [campo]: val };
    setAgenda(n);
  };
  const eliminarAgendaItem = (i: number) => setAgenda(agenda.filter((_, idx) => idx !== i));

  useEffect(() => {
    const fetchTaller = async () => {
      if (!id) return;
      const docRef = doc(db, 'taller', id);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setTaller(data);

        // Llenar el formulario con los datos existentes
        setValue('nombre', data.nombre);

        // Manejar fecha (puede ser campo fecha o fechaInicio)
        if (data.fecha) {
          setValue('fecha', data.fecha.toDate().toISOString().split('T')[0]);
        } else if (data.fechaInicio) {
          setValue('fecha', data.fechaInicio.toDate().toISOString().split('T')[0]);
        }

        setValue('horaInicio', data.horaInicio || data.horario?.split('-')[0]?.trim() || '09:00');
        setValue('horaTermino', data.horaTermino || data.horario?.split('-')[1]?.trim() || '18:00');
        setValue('lugar', data.lugar);
        setValue('direccion', data.direccion || '');
        setValue('ciudad', data.ciudad || '');
        setValue('organizadorPrincipal', data.organizadorPrincipal || data.expositor || '');

        // Datos del responsable
        if (data.responsable) {
          setValue('responsableNombre', data.responsable.nombre);
          setValue('responsableCargo', data.responsable.cargo);
          setValue('responsableCorreo', data.responsable.correo || '');
          setValue('responsableTelefono', data.responsable.telefono || '');
        } else {
          setValue('responsableNombre', '');
          setValue('responsableCargo', '');
        }

        setValue('tipoEvento', data.tipoEvento || 'presencial');
        setTipoEventoActual(data.tipoEvento || 'presencial');
        setValue('enlaceVirtual', data.enlaceVirtual || '');
        setValue('objetivo', data.objetivo || '');
        setValue('publicoObjetivo', data.publicoObjetivo || '');
        setValue('descripcionGeneral', data.descripcionGeneral || '');
        setValue('cupoMaximo', data.cupoMaximo || data.cupo || 50);
        setValue('tipoCosto', data.tipoCosto || 'gratuito');
        setTipoCostoActual(data.tipoCosto || 'gratuito');
        setValue('precio', data.precio || 0);
        setValue('moneda', data.moneda || 'MXN');
        setValue('creditos', data.creditos || 0);

        // Arrays y campos avanzados
        const actividadesData: string[] = Array.isArray(data.actividades) ? data.actividades : [];
        setActividades(actividadesData);
        setValue('actividades', actividadesData);
        setValue('otraActividad', data.otraActividad || '');
        setPonentes(Array.isArray(data.ponentes) ? data.ponentes : []);
        setAgenda(Array.isArray(data.agenda) ? data.agenda : []);
        setPatrocinadores(Array.isArray(data.patrocinadores) ? data.patrocinadores : []);

        // Registro
        const reqReg = data.requiereRegistro === true;
        setRequiereRegistroActual(reqReg);
        setValue('requiereRegistro', reqReg);
        setValue('infoRecopilar', data.infoRecopilar || '');

        // Datos bancarios (si es de pago)
        setValue('bancoTitular', data.datosPago?.titular || '');
        setValue('bancoNombre', data.datosPago?.banco || '');
        setValue('bancoCuenta', data.datosPago?.cuenta || '');
        setValue('bancoClabe', data.datosPago?.clabe || '');
        setValue('bancoReferencia', data.datosPago?.referencia || '');
        setValue('bancoInstrucciones', data.datosPago?.instrucciones || '');

        // Comunicación / enlaces
        setValue('elementosDestacados', data.elementosDestacados || '');
        setValue('linkRegistro', data.enlaces?.registro || '');
        setValue('landingPage', data.enlaces?.landingPage || '');
        setValue('eventoFacebook', data.enlaces?.facebook || '');
        // Expandir sección si ya hay algún dato de comunicación
        const hayComunicacion = !!(data.elementosDestacados || data.enlaces?.registro || data.enlaces?.landingPage || data.enlaces?.facebook);
        setMostrarEnlaces(hayComunicacion);

        // Establecer estado actual
        setEstadoManual(data.estado || 'proximo');

        // Guardar URL del banner actual si existe
        if (data.bannerUrl) {
          setCurrentBannerUrl(data.bannerUrl);
        }
      }
      setLoading(false);
    };
    fetchTaller();
  }, [id, setValue]);

  const onSubmit = async (data: any) => {
    if (!id) return;
    const docRef = doc(db, 'taller', id);

    try {
      // Subir banner si hay uno nuevo (tolerante a fallos de CORS/red)
      let bannerUrl = currentBannerUrl;
      let bannerError = '';
      if (bannerFile) {
        try {
          const formData = new FormData();
          formData.append('nombreTaller', data.nombre);
          formData.append('fecha', data.fecha);
          formData.append('banner', bannerFile);

          const res = await fetch(process.env.REACT_APP_UPLOAD_BANNER_URL || 'https://ampirivieranayarit.com/modulob/backend/upload-banner.php', {
            method: 'POST',
            body: formData
          });

          if (!res.ok) throw new Error(`Upload respondió ${res.status}`);
          const json = await res.json();
          if (json?.url) {
            bannerUrl = json.url;
          } else {
            throw new Error(json?.error || 'Respuesta sin URL');
          }
        } catch (uploadErr: any) {
          console.warn('Upload de banner falló, se continúa sin cambiar banner:', uploadErr);
          bannerError = uploadErr?.message || 'No se pudo subir el banner';
        }
      }

      // Calcular estado automático si no es cancelado
      let estadoFinal = estadoManual;
      if (estadoManual !== 'cancelado') {
        estadoFinal = calcularEstadoTaller(
          new Date(data.fecha),
          data.horaInicio,
          data.horaTermino,
          estadoManual
        );
      }

      const updateData = {
        ...taller, // Mantener datos existentes no gestionados por el form
        nombre: data.nombre,
        fecha: new Date(data.fecha),
        horaInicio: data.horaInicio,
        horaTermino: data.horaTermino,
        lugar: data.lugar,
        direccion: data.direccion,
        ciudad: data.ciudad,
        organizadorPrincipal: data.organizadorPrincipal,
        responsable: {
          nombre: data.responsableNombre,
          cargo: data.responsableCargo,
          correo: data.responsableCorreo || '',
          telefono: data.responsableTelefono || ''
        },
        tipoEvento: data.tipoEvento,
        enlaceVirtual: (data.tipoEvento === 'virtual' || data.tipoEvento === 'hibrido') ? (data.enlaceVirtual || '') : '',
        objetivo: data.objetivo,
        publicoObjetivo: data.publicoObjetivo,
        descripcionGeneral: data.descripcionGeneral,
        cupoMaximo: parseInt(data.cupoMaximo),
        tipoCosto: data.tipoCosto,
        precio: data.tipoCosto === 'pago' ? parseFloat(data.precio) : 0,
        moneda: data.tipoCosto === 'pago' ? data.moneda : '',
        datosPago: data.tipoCosto === 'pago' ? {
          titular: data.bancoTitular || '',
          banco: data.bancoNombre || '',
          cuenta: data.bancoCuenta || '',
          clabe: data.bancoClabe || '',
          referencia: data.bancoReferencia || '',
          instrucciones: data.bancoInstrucciones || ''
        } : null,
        creditos: parseInt(data.creditos) || 0,
        // Arrays completos gestionados desde el form
        actividades,
        otraActividad: data.otraActividad || '',
        ponentes: ponentes.filter(p => p.nombre),
        agenda: agenda.filter(a => a.horario && a.actividad),
        patrocinadores: patrocinadores.filter(p => p.nombre),
        // Registro
        requiereRegistro: data.requiereRegistro === true || data.requiereRegistro === 'true',
        infoRecopilar: (data.requiereRegistro === true || data.requiereRegistro === 'true') ? (data.infoRecopilar || '') : '',
        // Comunicación
        elementosDestacados: data.elementosDestacados || '',
        enlaces: {
          registro: data.linkRegistro || '',
          landingPage: data.landingPage || '',
          facebook: data.eventoFacebook || ''
        },
        estado: estadoFinal,
        bannerUrl: bannerUrl,
        fechaModificacion: new Date()
      };

      await updateDoc(docRef, updateData);
      await Swal.fire({
        icon: bannerError ? 'warning' : 'success',
        title: bannerError ? 'Actualizado (sin cambiar banner)' : '¡Éxito!',
        html: bannerError
          ? `<p>Los cambios se guardaron, pero no se pudo subir el banner nuevo.</p><p class="text-warning small">${bannerError}</p>`
          : 'Taller actualizado correctamente',
        confirmButtonColor: bannerError ? '#ffc107' : '#3085d6'
      });
      navigate('/admin-talleres');
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al actualizar taller',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (loading) {
    return (
      <>
        <UserNav />
        <PageTitle
          title="Editar Taller"
          subtitle="Cargando datos del taller..."
        />
        <section className="gray-simple">
          <div className="container py-5">
            <LoadingMessage message="Cargando datos del taller..." subMessage="Por favor espera" />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  return (
    <>
      <UserNav />
      <PageTitle
        title="Editar Taller"
        subtitle="Actualiza la información del taller"
      />

      <section className="gray-simple">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4">
                <form onSubmit={handleSubmit(onSubmit)}>
                  {/* Información Básica */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Información Básica</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Nombre del Evento *</label>
                          <input {...register('nombre')} className="form-control" />
                          {errors.nombre && <p className="text-danger small">{errors.nombre.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Fecha del Evento *</label>
                          <input type="date" {...register('fecha')} className="form-control" />
                          {errors.fecha && <p className="text-danger small">{errors.fecha.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Hora de Inicio *</label>
                          <input {...register('horaInicio')} className="form-control" placeholder="09:00" />
                          {errors.horaInicio && <p className="text-danger small">{errors.horaInicio.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Hora de Término *</label>
                          <input {...register('horaTermino')} className="form-control" placeholder="18:00" />
                          {errors.horaTermino && <p className="text-danger small">{errors.horaTermino.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <div className="photo-upload">
                            <label>Banner del Evento</label>
                            {currentBannerUrl && (
                              <div className="mb-2">
                                <img
                                  src={currentBannerUrl}
                                  alt="Banner actual"
                                  className="img-thumbnail"
                                  style={{ maxHeight: '150px' }}
                                />
                                <p className="small text-muted">Banner actual</p>
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="form-control"
                              onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                            />
                            <small className="text-muted">
                              {currentBannerUrl ? 'Selecciona una nueva imagen para reemplazar el banner actual' : 'Agrega un banner promocional'}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Estado del Evento */}
                  <div className="card mb-4">
                    <div className="card-header bg-warning">
                      <h5 className="mb-0"><i className="fa-solid fa-circle-info me-2"></i>Estado del Evento</h5>
                    </div>
                    <div className="card-body">
                      <div className="alert alert-info mb-3">
                        <strong>ℹ️ Nota:</strong> El estado se actualiza automáticamente basándose en las fechas del evento:
                        <ul className="mb-0 mt-2">
                          <li><strong>Próximo:</strong> Cuando la fecha del evento aún no ha llegado</li>
                          <li><strong>En Curso:</strong> Durante el día y horario del evento</li>
                          <li><strong>Finalizado:</strong> Después de que termina el evento</li>
                          <li><strong>Cancelado:</strong> Solo puede establecerse manualmente</li>
                        </ul>
                      </div>

                      <div className="row">
                        <div className="col-lg-6 mb-3">
                          <label className="form-label fw-bold">Estado Actual</label>
                          <select
                            className="form-select"
                            value={estadoManual}
                            onChange={(e) => setEstadoManual(e.target.value)}
                          >
                            <option value="proximo">📅 Próximo</option>
                            <option value="en_curso">▶️ En Curso</option>
                            <option value="finalizado">✅ Finalizado</option>
                            <option value="cancelado">❌ Cancelado</option>
                          </select>
                          <small className="text-muted">
                            {estadoManual === 'cancelado'
                              ? '⚠️ Este taller está marcado como cancelado'
                              : '✨ El estado se actualizará automáticamente al guardar según las fechas'}
                          </small>
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label className="form-label fw-bold">Vista Previa del Estado</label>
                          <div>
                            {estadoManual === 'proximo' && (
                              <span className="badge bg-primary fs-6">📅 Próximo</span>
                            )}
                            {estadoManual === 'en_curso' && (
                              <span className="badge bg-success fs-6">▶️ En Curso</span>
                            )}
                            {estadoManual === 'finalizado' && (
                              <span className="badge bg-secondary fs-6">✅ Finalizado</span>
                            )}
                            {estadoManual === 'cancelado' && (
                              <span className="badge bg-danger fs-6">❌ Cancelado</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ubicación */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Ubicación</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Lugar/Sede *</label>
                          <input {...register('lugar')} className="form-control" />
                          {errors.lugar && <p className="text-danger small">{errors.lugar.message}</p>}
                        </div>

                        <div className="col-lg-8 mb-3">
                          <label>Dirección *</label>
                          <input {...register('direccion')} className="form-control" />
                          {errors.direccion && <p className="text-danger small">{errors.direccion.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Ciudad *</label>
                          <input {...register('ciudad')} className="form-control" />
                          {errors.ciudad && <p className="text-danger small">{errors.ciudad.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Organización */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Organización</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Organizador Principal *</label>
                          <input {...register('organizadorPrincipal')} className="form-control" />
                          {errors.organizadorPrincipal && <p className="text-danger small">{errors.organizadorPrincipal.message}</p>}
                        </div>

                        <div className="col-12"><h6>Responsable del Evento</h6></div>

                        <div className="col-lg-6 mb-3">
                          <label>Nombre *</label>
                          <input {...register('responsableNombre')} className="form-control" />
                          {errors.responsableNombre && <p className="text-danger small">{errors.responsableNombre.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Cargo *</label>
                          <input {...register('responsableCargo')} className="form-control" />
                          {errors.responsableCargo && <p className="text-danger small">{errors.responsableCargo.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Correo (opcional)</label>
                          <input type="email" {...register('responsableCorreo')} className="form-control" />
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Teléfono (opcional)</label>
                          <input {...register('responsableTelefono')} className="form-control" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detalles del Evento */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Detalles del Evento</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-4 mb-3">
                          <label>Tipo de Evento *</label>
                          <select
                            {...register('tipoEvento')}
                            className="form-control"
                            onChange={(e) => setTipoEventoActual(e.target.value)}
                          >
                            <option value="presencial">Presencial</option>
                            <option value="virtual">Virtual</option>
                            <option value="hibrido">Híbrido</option>
                          </select>
                          {errors.tipoEvento && <p className="text-danger small">{errors.tipoEvento.message}</p>}
                        </div>

                        {/* Campo de enlace virtual - solo visible para eventos virtuales o híbridos */}
                        {(tipoEventoActual === 'virtual' || tipoEventoActual === 'hibrido') && (
                          <div className="col-lg-8 mb-3">
                            <label>
                              Enlace de la Sesión Virtual {tipoEventoActual === 'virtual' ? '*' : '(opcional)'}
                            </label>
                            <input
                              {...register('enlaceVirtual')}
                              className="form-control"
                              placeholder="https://zoom.us/j/... o https://teams.microsoft.com/..."
                            />
                            <small className="text-muted">
                              Link de Zoom, Teams, Google Meet u otra plataforma
                            </small>
                            {errors.enlaceVirtual && <p className="text-danger small">{errors.enlaceVirtual.message}</p>}
                          </div>
                        )}

                        <div className="col-lg-4 mb-3">
                          <label>Cupo Máximo *</label>
                          <input type="number" {...register('cupoMaximo')} className="form-control" />
                          {errors.cupoMaximo && <p className="text-danger small">{errors.cupoMaximo.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Créditos AMPI</label>
                          <input type="number" {...register('creditos')} className="form-control" defaultValue="0" />
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Objetivo del Evento *</label>
                          <textarea {...register('objetivo')} className="form-control" rows={3} />
                          {errors.objetivo && <p className="text-danger small">{errors.objetivo.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Público Objetivo (Dirigido a) *</label>
                          <select {...register('publicoObjetivo')} className="form-select">
                            <option value="">-- Selecciona --</option>
                            <option value="afiliados">Solo Afiliados</option>
                            <option value="asociados">Solo Asociados</option>
                            <option value="afiliados_asociados">Afiliados y Asociados</option>
                            <option value="abierto_publico">Abierto al Público</option>
                          </select>
                          <small className="text-muted">Los eventos "Abierto al Público" aceptan inscripciones sin cuenta; los demás solo miembros autenticados.</small>
                          {errors.publicoObjetivo && <p className="text-danger small">{errors.publicoObjetivo.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Descripción General *</label>
                          <textarea {...register('descripcionGeneral')} className="form-control" rows={4} />
                          {errors.descripcionGeneral && <p className="text-danger small">{errors.descripcionGeneral.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actividades */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Actividades Incluidas</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-12 mb-3">
                          <label>Selecciona las actividades que incluirá el evento</label>
                          <div className="row">
                            {actividadesOpciones.map((act) => (
                              <div key={act} className="col-md-6 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`edit-act-${act}`}
                                    checked={actividades.includes(act)}
                                    onChange={() => handleActividadChange(act)}
                                  />
                                  <label className="form-check-label" htmlFor={`edit-act-${act}`}>{act}</label>
                                </div>
                              </div>
                            ))}
                          </div>
                          {errors.actividades && <p className="text-danger small">{errors.actividades.message}</p>}
                        </div>
                        <div className="col-lg-12 mb-3">
                          <label>Otra actividad (especifica)</label>
                          <input {...register('otraActividad')} className="form-control" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ponentes */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Ponentes Confirmados</h5>
                      <button type="button" className="btn btn-sm btn-light" onClick={agregarPonente}>
                        <Plus size={16} className="me-1" /> Agregar Ponente
                      </button>
                    </div>
                    <div className="card-body">
                      {ponentes.length === 0 ? (
                        <p className="text-muted">No hay ponentes agregados.</p>
                      ) : (
                        ponentes.map((p, i) => (
                          <div key={i} className="border rounded p-3 mb-3">
                            <div className="row">
                              <div className="col-lg-6 mb-3">
                                <label>Nombre completo</label>
                                <input className="form-control" value={p.nombre}
                                  onChange={(e) => actualizarPonente(i, 'nombre', e.target.value)} />
                              </div>
                              <div className="col-lg-6 mb-3">
                                <label>Cargo</label>
                                <input className="form-control" value={p.cargo}
                                  onChange={(e) => actualizarPonente(i, 'cargo', e.target.value)} />
                              </div>
                              <div className="col-lg-6 mb-3">
                                <label>Empresa</label>
                                <input className="form-control" value={p.empresa}
                                  onChange={(e) => actualizarPonente(i, 'empresa', e.target.value)} />
                              </div>
                              <div className="col-lg-6 mb-3">
                                <label>Horario</label>
                                <input className="form-control" value={p.horario}
                                  onChange={(e) => actualizarPonente(i, 'horario', e.target.value)} />
                              </div>
                              <div className="col-lg-12 mb-3">
                                <label>Tema o título de la ponencia</label>
                                <input className="form-control" value={p.tema}
                                  onChange={(e) => actualizarPonente(i, 'tema', e.target.value)} />
                              </div>
                              <div className="col-12">
                                <button type="button" className="btn btn-sm btn-danger"
                                  onClick={() => eliminarPonente(i)}>
                                  <Trash2 size={16} className="me-1" /> Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Agenda */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Agenda Tentativa</h5>
                      <button type="button" className="btn btn-sm btn-light" onClick={agregarAgendaItem}>
                        <Plus size={16} className="me-1" /> Agregar Actividad
                      </button>
                    </div>
                    <div className="card-body">
                      {agenda.length === 0 ? (
                        <p className="text-muted">No hay actividades en la agenda.</p>
                      ) : (
                        agenda.map((item, i) => (
                          <div key={i} className="border rounded p-3 mb-3">
                            <div className="row">
                              <div className="col-lg-3 mb-3">
                                <label>Horario</label>
                                <input className="form-control" value={item.horario}
                                  onChange={(e) => actualizarAgendaItem(i, 'horario', e.target.value)} />
                              </div>
                              <div className="col-lg-4 mb-3">
                                <label>Actividad</label>
                                <input className="form-control" value={item.actividad}
                                  onChange={(e) => actualizarAgendaItem(i, 'actividad', e.target.value)} />
                              </div>
                              <div className="col-lg-5 mb-3">
                                <label>Descripción</label>
                                <input className="form-control" value={item.descripcion}
                                  onChange={(e) => actualizarAgendaItem(i, 'descripcion', e.target.value)} />
                              </div>
                              <div className="col-12">
                                <button type="button" className="btn btn-sm btn-danger"
                                  onClick={() => eliminarAgendaItem(i)}>
                                  <Trash2 size={16} className="me-1" /> Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Registro y Costo */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Registro y Costo</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-6 mb-3">
                          <label>¿Requiere registro previo?</label>
                          <select
                            {...register('requiereRegistro')}
                            className="form-control"
                            onChange={(e) => setRequiereRegistroActual(e.target.value === 'true')}
                          >
                            <option value="false">No</option>
                            <option value="true">Sí</option>
                          </select>
                        </div>
                        {requiereRegistroActual && (
                          <div className="col-lg-6 mb-3">
                            <label>¿Qué información se recopilará?</label>
                            <input {...register('infoRecopilar')} className="form-control"
                              placeholder="Ej. Nombre, correo, teléfono, empresa" />
                          </div>
                        )}

                        <div className="col-lg-4 mb-3">
                          <label>Tipo de Costo *</label>
                          <select
                            {...register('tipoCosto')}
                            className="form-control"
                            onChange={(e) => setTipoCostoActual(e.target.value)}
                          >
                            <option value="gratuito">Gratuito</option>
                            <option value="pago">Con costo</option>
                          </select>
                          {errors.tipoCosto && <p className="text-danger small">{errors.tipoCosto.message}</p>}
                        </div>

                        {tipoCostoActual === 'pago' && (
                          <>
                            <div className="col-lg-4 mb-3">
                              <label>Precio *</label>
                              <input type="number" {...register('precio')} className="form-control" />
                            </div>
                            <div className="col-lg-4 mb-3">
                              <label>Moneda *</label>
                              <select {...register('moneda')} className="form-control">
                                <option value="MXN">MXN</option>
                                <option value="USD">USD</option>
                              </select>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Información Bancaria — solo si es de pago */}
                  {tipoCostoActual === 'pago' && (
                    <div className="card mb-4">
                      <div className="card-header bg-primary text-white">
                        <h5 className="mb-0">Información Bancaria para el Pago</h5>
                      </div>
                      <div className="card-body">
                        <div className="alert alert-info small mb-3">
                          Estos datos se mostrarán al usuario al inscribirse para que realice el depósito y suba su comprobante.
                        </div>
                        <div className="row">
                          <div className="col-lg-6 mb-3">
                            <label>Titular de la cuenta</label>
                            <input {...register('bancoTitular')} className="form-control" placeholder="Ej. AMPI Riviera Nayarit A.C." />
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label>Banco</label>
                            <input {...register('bancoNombre')} className="form-control" placeholder="Ej. BBVA" />
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label>Número de Cuenta</label>
                            <input {...register('bancoCuenta')} className="form-control" />
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label>CLABE Interbancaria</label>
                            <input {...register('bancoClabe')} className="form-control" maxLength={18} />
                          </div>
                          <div className="col-lg-12 mb-3">
                            <label>Referencia sugerida</label>
                            <input {...register('bancoReferencia')} className="form-control" />
                          </div>
                          <div className="col-lg-12 mb-3">
                            <label>Instrucciones adicionales</label>
                            <textarea {...register('bancoInstrucciones')} className="form-control" rows={2} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Patrocinadores */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Aliados, Patrocinadores y Expositores</h5>
                      <button type="button" className="btn btn-sm btn-light" onClick={agregarPatrocinador}>
                        <Plus size={16} className="me-1" /> Agregar
                      </button>
                    </div>
                    <div className="card-body">
                      {patrocinadores.length === 0 ? (
                        <p className="text-muted">No hay patrocinadores agregados.</p>
                      ) : (
                        patrocinadores.map((pat, i) => (
                          <div key={i} className="border rounded p-3 mb-3">
                            <div className="row">
                              <div className="col-lg-4 mb-3">
                                <label>Nombre</label>
                                <input className="form-control" value={pat.nombre}
                                  onChange={(e) => actualizarPatrocinador(i, 'nombre', e.target.value)} />
                              </div>
                              <div className="col-lg-3 mb-3">
                                <label>Tipo</label>
                                <select
                                  className="form-control"
                                  value={pat.tipo}
                                  onChange={(e) => actualizarPatrocinador(i, 'tipo', e.target.value)}
                                >
                                  <option value="aliado">Aliado</option>
                                  <option value="patrocinador">Patrocinador</option>
                                  <option value="expositor">Expositor</option>
                                </select>
                              </div>
                              <div className="col-lg-5 mb-3">
                                <label>Link del sitio web</label>
                                <input className="form-control" value={pat.link}
                                  onChange={(e) => actualizarPatrocinador(i, 'link', e.target.value)} />
                              </div>
                              <div className="col-12">
                                <button type="button" className="btn btn-sm btn-danger"
                                  onClick={() => eliminarPatrocinador(i)}>
                                  <Trash2 size={16} className="me-1" /> Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Comunicación y Enlaces — colapsable */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Comunicación y Enlaces <small className="text-white-50">(opcional)</small></h5>
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={() => setMostrarEnlaces(!mostrarEnlaces)}
                      >
                        {mostrarEnlaces ? 'Ocultar' : 'Agregar enlaces'}
                      </button>
                    </div>
                    {mostrarEnlaces && (
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-12 mb-3">
                            <label>Elementos clave a destacar</label>
                            <textarea {...register('elementosDestacados')} className="form-control" rows={3} />
                          </div>
                          <div className="col-lg-12 mb-3">
                            <label>Link de registro</label>
                            <input {...register('linkRegistro')} className="form-control" placeholder="https://..." />
                            {errors.linkRegistro && <p className="text-danger small">{errors.linkRegistro.message}</p>}
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label>Landing page</label>
                            <input {...register('landingPage')} className="form-control" placeholder="https://..." />
                            {errors.landingPage && <p className="text-danger small">{errors.landingPage.message}</p>}
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label>Evento en Facebook</label>
                            <input {...register('eventoFacebook')} className="form-control" placeholder="https://facebook.com/events/..." />
                            {errors.eventoFacebook && <p className="text-danger small">{errors.eventoFacebook.message}</p>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-group mt-4">
                    <button type="submit" className="btn btn-primary btn-lg me-3">
                      Guardar Cambios
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-lg"
                      onClick={() => navigate('/admin-talleres')}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}